"use client";

import React, { useState } from "react";
import { ArrowRight, Terminal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const solutions = [{
  id: "the-grand-manor",
  title: "The Grand Manor",
  description: "A historic estate offering timeless elegance and luxury suites. Located just minutes from our ceremony venue with exclusive rates for our wedding guests.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/luxury_hotel_manor.png",
  tags: ["0.5 Miles Away", "Shuttle Provided", "Breakfast Included", "Wedding Block"],
  link: "/TheBigDayPage"
}, {
  id: "riverside-boutique",
  title: "Riverside Boutique",
  description: "Modern sophistication meets scenic views. This boutique hotel features a rooftop lounge and is perfectly situated for exploring the local downtown area.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/boutique_hotel_riverside.png",
  tags: ["1.2 Miles Away", "Rooftop Bar", "Valet Parking", "Pet Friendly"],
  link: "/TheBigDayPage"
}, {
  id: "stone-creek-inn",
  title: "Stone Creek Inn",
  description: "A charming and cozy retreat for those seeking a quieter stay. Enjoy the rustic beauty of the countryside while remaining close to all the festivities.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/rustic_inn_stay.png",
  tags: ["3.0 Miles Away", "Garden Views", "Quiet Zone", "Local Charm"],
  link: "/TheBigDayPage"
}];
export default function OurSpecializedSolutions() {
  const [activeId, setActiveId] = useState(solutions[0].id);
  return <section data-section-id="221980" id="guest-accommodations" className="bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:3rem_3rem] opacity-5 pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="mb-16">
          <div className="flex items-center gap-2 mb-4">
            <Terminal className="size-5 text-primary" />
            <span className="font-default text-sm tracking-widest text-primary uppercase">Travel & Stay</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tight text-foreground">
            Guest Accommodations
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl text-lg font-default">
            We have curated a selection of nearby hotels to ensure your stay is as comfortable and elegant as our celebration.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {}
          <div className="lg:col-span-4 flex flex-col gap-2 relative">
            {}
            <div className="absolute left-0 top-0 bottom-0 w-px bg-border/30 hidden lg:block" />

            {solutions.map((solution, i) => {
            const isActive = activeId === solution.id;
            return <Button key={solution.id} data-index={i} variant="ghost" onClick={() => setActiveId(solution.id)} onMouseEnter={() => setActiveId(solution.id)} className={`group relative flex flex-col items-start text-left px-6 py-8 h-auto transition-all duration-300 border-l-2 lg:border-l-4 justify-start rounded-md ${isActive ? "border-primary bg-primary/5" : "border-transparent hover:border-border hover:bg-muted/50"}`}>
                  <span className={`text-3xl md:text-4xl font-default font-bold tracking-tight transition-colors duration-300 ${isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground/80"}`}>
                    {solution.title}
                  </span>
                  <span className={`mt-2 font-default text-xs tracking-widest uppercase transition-all duration-300 ${isActive ? "text-primary opacity-100 translate-y-0" : "text-muted-foreground opacity-0 -translate-y-2"}`}>
                    Selected Option
                  </span>
                </Button>;
          })}
          </div>

          {}
          <div className="lg:col-span-8 relative min-h-[550px] md:min-h-[600px] w-full">
            {solutions.map((solution, i) => {
            const isActive = activeId === solution.id;
            return <div key={solution.id} data-index={i} className={`absolute inset-0 w-full transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isActive ? "opacity-100 translate-x-0 z-10 pointer-events-auto" : "opacity-0 translate-x-16 z-0 pointer-events-none"}`}>
                  <Card className="h-full border-border/40 bg-card/60 backdrop-blur-md shadow-xl overflow-hidden flex flex-col rounded-md">
                    {}
                    <div className="h-8 border-b border-border/40 bg-muted/30 flex items-center px-4 gap-2 shrink-0">
                      <div className="size-2 rounded-md bg-primary/40" />
                      <div className="size-2 rounded-md bg-primary/20" />
                      <div className="size-2 rounded-md bg-primary/10" />
                      <span className="ml-auto font-default text-[10px] text-muted-foreground uppercase tracking-widest">
                        TRAVEL.INFO.{solution.id.toUpperCase()}
                      </span>
                    </div>

                    <div className="relative h-48 md:h-72 w-full bg-muted/10 overflow-hidden border-b border-border/40 shrink-0">
                      <img src={solution.image} alt={`${solution.title} luxury accommodation`} className="w-full h-full object-cover object-center opacity-90 transition-all duration-700 hover:scale-105" />
                      {}
                      <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                    </div>

                    <CardHeader className="pb-4 shrink-0">
                      <CardTitle className="text-2xl font-default text-foreground">
                        {solution.title}
                      </CardTitle>
                      <CardDescription className="text-base text-muted-foreground leading-relaxed max-w-2xl font-default">
                        {solution.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="flex-1 flex flex-col justify-between gap-6">
                      <div className="flex flex-wrap gap-2">
                        {solution.tags.map((tag, tagIndex) => <Badge key={tagIndex} data-index={tagIndex} variant="outline" className="font-default text-xs border-primary/20 text-primary bg-primary/5 rounded-md">
                            {tag}
                          </Badge>)}
                      </div>

                      <div className="pt-4 mt-auto border-t border-border/20">
                        <Button asChild variant="ghost" className="group text-primary hover:text-primary hover:bg-primary/5 -ml-4 rounded-md">
                          <Link to={solution.link}>
                            Book with Wedding Code
                            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}